import { useNavigate } from "react-router-dom";
import { X, Users, Bed, Wifi, Snowflake, MapPin, AlertTriangle, KeyRound, Layers, ArrowUpDown, UserCheck, Home, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { STATUS_COLORS, CLEANING_RING, PLATFORM_META } from "@/lib/constants";

function Chip({ className, children, testid }) {
  return (
    <span data-testid={testid}
          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${className}`}>
      {children}
    </span>
  );
}

export default function MapAptCard({ apt, onClose }) {
  const navigate = useNavigate();
  if (!apt) return null;
  const status = STATUS_COLORS[apt.color_status] || STATUS_COLORS.ok;
  const clean = CLEANING_RING[apt.cleaning_company] || CLEANING_RING["Non renseigné"];
  const plats = (apt.platforms || []).map((p) => p.platform);
  const photoCount = (apt.photos?.length || 0) || (apt.photo_url ? 1 : 0);
  const kh = apt.key_handover || (apt.keynest ? "KeyNest" : apt.keybox ? "Boîte à clés" : "Check-in physique");
  const KhIcon = kh === "Check-in physique" ? UserCheck : KeyRound;

  return (
    <div data-testid="map-apt-card"
         className="absolute z-[1001] top-[4.5rem] right-3 w-[350px] max-w-[calc(100vw-1.5rem)] max-md:top-auto max-md:bottom-[4.5rem] max-md:left-2 max-md:right-2 max-md:w-auto bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[calc(100%-7rem)]">
      <div className="relative flex-shrink-0">
        {apt.photo_url ? (
          <img src={apt.photo_url} alt={apt.name} className="w-full h-36 object-cover" />
        ) : (
          <div className="w-full h-24 bg-slate-100 flex items-center justify-center">
            <Home className="w-8 h-8 text-slate-300" />
          </div>
        )}
        {photoCount > 0 && (
          <span data-testid="map-apt-card-photocount"
                className="absolute bottom-2 right-2 bg-slate-900/80 text-white text-xs px-2 py-0.5 rounded-md flex items-center gap-1">
            <Camera className="w-3 h-3" />{photoCount}
          </span>
        )}
        <button onClick={onClose} data-testid="map-apt-card-close"
                className="absolute top-2.5 right-2.5 bg-white rounded-full p-1.5 shadow hover:bg-slate-50">
          <X className="w-4 h-4 text-slate-700" />
        </button>
      </div>
      <div className="p-4 space-y-3 overflow-y-auto thin-scroll">
        <div>
          <h3 className="text-base font-bold font-display text-slate-900 leading-tight">{apt.name}</h3>
          <div className="flex items-center gap-1 text-sm text-slate-500 mt-0.5">
            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />{apt.address}, {apt.city} {apt.arrondissement}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Chip testid="badge-severity" className={status.cls}>{status.label}</Chip>
          {apt.open_problems > 0 && (
            <Chip testid="badge-anomalies" className="bg-red-50 text-red-700 border-red-200">
              <AlertTriangle className="w-3 h-3" />{apt.open_problems} anomalie{apt.open_problems > 1 ? "s" : ""}
            </Chip>
          )}
          {apt.mobility_lease && (
            <Chip testid="badge-mobility" className="bg-indigo-50 text-indigo-700 border-indigo-200">
              <KeyRound className="w-3 h-3" />Bail mobilité
            </Chip>
          )}
          <Chip testid="badge-checkin" className="bg-slate-100 text-slate-700 border-slate-200">
            <KhIcon className="w-3 h-3" />{kh}
          </Chip>
          <Chip testid="badge-cleaning" className={clean.badge}>{apt.cleaning_company}</Chip>
          {apt.referent && (
            <Chip testid="badge-referent" className="bg-white text-slate-700 border-slate-200">{apt.referent}</Chip>
          )}
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
          <div className="flex items-center gap-2 text-slate-700"><Users className="w-4 h-4 text-slate-400 flex-shrink-0" /> {apt.max_guests} voyageurs</div>
          <div className="flex items-center gap-2 text-slate-700"><Bed className="w-4 h-4 text-slate-400 flex-shrink-0" /> {apt.sleepings} couchages</div>
          <div className="flex items-center gap-2 text-slate-700"><Wifi className={`w-4 h-4 flex-shrink-0 ${apt.wifi_available ? "text-emerald-500" : "text-slate-300"}`} /> {apt.wifi_available ? "Wi-Fi" : "Pas de Wi-Fi"}</div>
          <div className="flex items-center gap-2 text-slate-700"><Snowflake className={`w-4 h-4 flex-shrink-0 ${apt.ac_available ? "text-sky-500" : "text-slate-300"}`} /> {apt.ac_available ? "Clim." : "Pas de clim."}</div>
          <div className="flex items-center gap-2 text-slate-700"><Layers className="w-4 h-4 text-slate-400 flex-shrink-0" /> {apt.floor ? `Étage ${apt.floor}` : "Étage —"}</div>
          <div className="flex items-center gap-2 text-slate-700"><ArrowUpDown className={`w-4 h-4 flex-shrink-0 ${apt.elevator ? "text-emerald-500" : "text-slate-300"}`} /> {apt.elevator ? "Ascenseur" : "Pas d'ascenseur"}</div>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-700" data-testid="map-apt-card-platforms">
          <Home className="w-4 h-4 text-slate-500 flex-shrink-0" />
          Plateformes :
          <span className="flex items-center gap-1.5 flex-wrap">
            {plats.length === 0 && <span className="text-slate-400">Aucune</span>}
            {plats.map((k) => (
              <span key={k} className="px-1.5 py-0.5 rounded text-[11px] font-bold text-white"
                    style={{ background: PLATFORM_META[k]?.color || "#64748B" }}>
                {PLATFORM_META[k]?.name || k}
              </span>
            ))}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2 pt-1">
          <Button onClick={() => navigate(`/apartments/${apt.id}`)}
                  data-testid="view-full-btn" className="bg-slate-900 hover:bg-slate-800 text-white">
            Voir la fiche
          </Button>
          <Button variant="outline" onClick={() => navigate(`/apartments/${apt.id}?tab=problems`)}
                  data-testid="view-problems-btn">
            Voir les anomalies
          </Button>
        </div>
      </div>
    </div>
  );
}
